import { FirebaseAuthentication, User } from '@capacitor-firebase/authentication';
import { Capacitor } from '@capacitor/core';

import { BadRequestError, BureauRetrievalError, NotFoundError, UnauthorizedError } from './error-types';
import { mapOnboardingToUserCreate } from '../utils/user.utils';
import { captureException } from '../analytics/sentry';
import { IOnboardingData } from '../providers/onboarding/onboarding.actions';
import { ROUTE_PATHS } from '../components/_pages/_utils/router.utils';
import { getUtmParams, trackSegment } from '../analytics/segment';
import { setFirebaseAnalyticsUserProperty, trackFirebaseEvent } from './firebase.service';
import { PLAN_PRICE, YEARLY_PLAN_PRICE } from '../utils';
import { IApplication } from './loan-application.types';
import { DEBT_GOAL } from '../components/Optimizer/types';
import { API_URL, BASE_URL, STRIPE_FREE_PRICE, STRIPE_MONTHLY_PRICE, STRIPE_ANNUAL_PRICE } from '../env';

export interface IUser {
  goal?: DEBT_GOAL;
  hasBureauAuthentication: boolean;
  bureauProviderRef: string;
  isSubscribed: string;
  source: string;
  email: string;
  employmentPayFrequency?: string;
  primaryPhone?: string;
  universityAttended?: string;
  currentCreditInfo?: {
    scoreNow: number;
  };
  loanInterestStatus?: boolean;
  solveLoans?: IApplication[];
  hasActiveFranklinLoan?: boolean;
  gclid?: string;
  fbclid?: string;
  isPremium: boolean;
  hasSsn?: boolean;
}

export interface ISpousalInformation {
  name: string;
  line1: string;
  line2?: string;
  city: string;
  stateAbbr: string;
  zipCode: string;
}

interface ICreateUserResponse {
  borrowerId: string;
  userId?: string;
  prequal0?: boolean;
}

/**
 * Creates a user account in Array, which needs all of the onboarding data, but specifically:
 *  - last 4 of ssn, firstName, lastName, dob, address
 * @param data - full user onboarding data
 * @param userToken - Firebase auth token
 */
export const createUser = async (data: IOnboardingData, firebaseToken: string): Promise<boolean> => {
  try {
    const createData = { ...data };
    if (!createData.source) delete createData.source;

    const response = await fetch(`${API_URL}/v1/borrowers`, {
      method: 'POST',
      headers: {
        'Access-Control-Allow-Origin': '*',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${firebaseToken}`,
      },
      body: JSON.stringify(mapOnboardingToUserCreate(createData)),
    });

    let result;

    switch (response.status) {
      case 200:
        result = (await response.json()) as ICreateUserResponse;

        if (!result.borrowerId) throw new Error('Failed to create user. Unexpected response from server');

        return result.prequal0;

      case 400:
        throw new BadRequestError('Provided invalid onboarding data to user create');
      case 401:
        throw new UnauthorizedError('Unauthorized');

      default:
        throw new Error('Failed to create user. Internal server error');
    }
  } catch (err) {
    captureException(err);

    throw err;
  }
};

/**
 * Get the full user data, including snapshots
 * @param userId - User ID in Firebase
 * @param userToken - Firebase auth token
 */
export const getUser = async (userId: string, userToken: string) => {
  const doFetch = async () => {
    const response = await fetch(`${API_URL}/v0/users/${userId}`, {
      method: 'GET',
      headers: {
        'Access-Control-Allow-Origin': '*',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${userToken}`,
      },
    });

    switch (response.status) {
      case 200:
        return await response.json();

      case 404:
        return null;

      case 401:
        throw new UnauthorizedError('Unauthorized');

      default:
        throw new Error(`Failed to get user. Internal server error: ${response?.status}`);
    }
  };

  try {
    return await doFetch();
  } catch (err) {
    // WebKit/iOS surfaces aborted body reads and backgrounded-tab drops as a
    // generic TypeError ("Load failed"). The request often succeeded server-side;
    // retry once before giving up.
    if (err instanceof TypeError) return await doFetch();
    throw err;
  }
};

/**
 * Fetches the HMAC used for Intercom Identity Verification.
 * The backend computes HMAC-SHA256(firebaseUid, INTERCOM_SECRET) and returns it.
 * Without this, anyone with a user's email can impersonate them in Intercom.
 */
export const getIntercomUserHash = async (userId: string, userToken: string): Promise<string | null> => {
  try {
    const response = await fetch(`${API_URL}/v1/users/${userId}/intercom-identity`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${userToken}`,
      },
    });

    if (response.status !== 200) return null;

    const { hash } = (await response.json()) as { hash: string };
    return hash ?? null;
  } catch (err) {
    captureException(err);
    return null;
  }
};

// Legacy
export const patchUser = async (payload: Partial<any>, firebaseUser: User) => {
  try {
    const response = await fetch(`${API_URL}/v0/users`, {
      method: 'PATCH',
      headers: {
        'Access-Control-Allow-Origin': '*',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${(await FirebaseAuthentication.getIdToken()).token}`,
      },
      body: JSON.stringify(payload),
    });

    switch (response.status) {
      case 200:
        return await response.json();

      case 404:
        throw new NotFoundError('User not found');

      default:
        throw new Error(`Failed to update user. Internal server error: ${response?.status}`);
    }
  } catch (err) {
    captureException(err);

    throw err;
  }
};

// v1 Endpoint
export const updateSpousalInformation = async (personId: string, spousalInformation: ISpousalInformation) => {
  try {
    const { token } = await FirebaseAuthentication.getIdToken();

    const response = await fetch(`${API_URL}/v1/borrowers/${personId}`, {
      method: 'PATCH',
      headers: {
        'Access-Control-Allow-Origin': '*',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ spousalInformation }),
    });

    switch (response.status) {
      case 200:
        return await response.json();

      case 404:
        throw new NotFoundError('User not found');

      default:
        throw new Error(`Failed to update user. Internal server error: ${response?.status}`);
    }
  } catch (err) {
    captureException(err);

    throw err;
  }
};

export const removeUser = async (firebaseRef: string, token: string) => {
  try {
    await fetch(`${API_URL}/v1/users`, {
      method: 'DELETE',
      headers: {
        'Access-Control-Allow-Origin': '*',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ firebaseRef }),
    });
  } catch (err) {
    captureException(err);

    throw err;
  }
};

export type BureauSecurityAnswer = {
  id: string;
  text: string;
};

export type BureauSecurityQuestion = {
  id: string;
  text: string;
  answers: BureauSecurityAnswer[];
};

interface IUpdateCreditReport {
  firebaseUser: User;
}

/**
 * Authenticate the user by providing answers to security questions
 * Returns additional questions if the user is not fully authenticated
 * If authenticated, processes their credit report without returning any data
 *
 * @param data - { authToken, answers: { questionId, answerId }[] }
 * @param userId - User ID in Firebase
 * @param userToken - Firebase auth token
 */
export const updateCreditReport = async ({ firebaseUser }: IUpdateCreditReport) => {
  const response = await fetch(`${API_URL}/v1/borrowers/${firebaseUser.uid}/bureau-provider/update-snapshot`, {
    method: 'POST',
    headers: {
      'Access-Control-Allow-Origin': '*',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${(await FirebaseAuthentication.getIdToken()).token}`,
    },
  });

  switch (response.status) {
    case 200:
      return;
    case 404:
      throw new NotFoundError('Failed to request credit report. User not found');
    case 502:
      throw new BureauRetrievalError(
        'Unable to retrieve your credit report from the bureau at this time. Please review your info.',
      );

    default:
      throw new Error('Unknown error occured while requesting credit report');
  }
};

export type StripeLineItem = {
  price: string;
  quantity: number;
};

export const STRIPE_FREE_ITEM = {
  price: STRIPE_FREE_PRICE || 'price_1PTmMeC4VcGzfkjKVoW5K1m1',
  quantity: 1,
  label: 'Solve Finance (via Debt Optimizer)',
  amount: 0.0,
};

export const STRIPE_MONTHLY_ITEM = {
  price: STRIPE_MONTHLY_PRICE || 'price_1Q5FbTC4VcGzfkjKEBUlsoIz',
  quantity: 1,
  label: 'Solve Finance (via Debt Optimizer)',
  amount: PLAN_PRICE,
};

export const STRIPE_ANNUAL_ITEM = {
  price: STRIPE_ANNUAL_PRICE,
  quantity: 1,
  label: 'Solve Finance (via Debt Optimizer)',
  amount: YEARLY_PLAN_PRICE,
};

/**
 *
 */
export const getStripeCheckout = async ({
  firebaseRef,
  token,
  lineItems,
  endpoint,
}): Promise<{ url: string | null; subscriptionId: string | null }> => {
  const response = await fetch(`${API_URL}/v0/users/${firebaseRef}/checkout`, {
    method: 'POST',
    headers: {
      'Access-Control-Allow-Origin': '*',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      line_items: lineItems,
      mode: 'subscription',
      from_app: Capacitor.getPlatform() !== 'web',
      local: BASE_URL,
      endpoint,
    }),
  });

  let result;
  let url;

  switch (response.status) {
    case 200:
      result = await response.json();
      url = result?.success ? result?.url : ROUTE_PATHS.SUBSCRIPTION + '?status=error';

      return { url, subscriptionId: result?.subscriptionId };
    default:
      throw new BadRequestError('Invalid request made to Stripe checkout setup');
  }
};

export const getStripeSubscriptionPaymentIntent = async ({
  firebaseRef,
  token,
  lineItems,
}): Promise<{
  paymentIntentClientSecret?: string;
  customerId: string;
  subscriptionId: string;
  ephemeralKey: string;
}> => {
  const response = await fetch(`${API_URL}/v1/users/${firebaseRef}/create-subscription`, {
    method: 'POST',
    headers: {
      'Access-Control-Allow-Origin': '*',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      line_items: lineItems,
      mode: 'subscription',
      from_app: Capacitor.getPlatform() !== 'web',
    }),
  });

  switch (response.status) {
    case 200:
      return response.json();
    default:
      throw new BadRequestError('Invalid request made to Stripe payment intent setup');
  }
};

export const requestUserRecommendations = async ({ firebaseUser, goal = 'MIN_MONTHLY', scoreUsed, onUpdate }) => {
  try {
    const queryString = new URLSearchParams({ goal }).toString();
    const response = await fetch(
      `${API_URL}/v0/users/${firebaseUser.uid}/recommendation?${queryString}${
        scoreUsed ? `&scoreUsed=${scoreUsed}` : ''
      }`,
      {
        method: 'GET',
        headers: {
          'Access-Control-Allow-Origin': '*',
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
          Authorization: `Bearer ${(await FirebaseAuthentication.getIdToken()).token}`,
        },
      },
    );

    switch (response.status) {
      case 200: {
        const contentType = response.headers.get('Content-Type');
        if (contentType && contentType.includes('text/event-stream')) {
          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';

          return new Promise((resolve) => {
            let firstPlan = null;

            const processStream = async () => {
              const keepReading = true;
              while (keepReading) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');

                while (lines.length > 1) {
                  const line = lines.shift().trim();
                  if (line.startsWith('data: ')) {
                    const plan = JSON.parse(line.slice(6));

                    if (!firstPlan) {
                      firstPlan = plan;
                      resolve(plan);
                    } else {
                      onUpdate?.(plan);
                    }

                    if (plan?.isCompleted) {
                      reader.cancel();
                      return;
                    }
                  }
                }
                buffer = lines.join('\n');
              }
            };

            processStream();
          });
        } else {
          return await response.json();
        }
      }
      case 204:
        return [];
      default:
        throw new Error('Unknown error occurred when requesting recommendations');
    }
  } catch (err) {
    captureException(err);
    throw err;
  }
};

let eventQueue = [];

export const processEventQueue = async (firebaseUser) => {
  await recordEvent({ eventName: null, firebaseUser, props: null });
};

interface IRecordEvent {
  eventName: string;
  firebaseUser: User;
  user?: IUser;
  props?: any;
}

export const recordEvent = async ({ eventName, firebaseUser, user, props }: IRecordEvent) => {
  if (eventName) eventQueue.push({ eventName, props: props || {} });

  try {
    if (firebaseUser) {
      const currQueue = [...eventQueue];
      eventQueue = [];

      const token = (await FirebaseAuthentication.getIdToken()).token;
      const params = await getUtmParams();

      for (let i = 0; i < currQueue.length; i++) {
        if (currQueue[i].props?.transaction_id) {
          currQueue[i].props.transaction_id = currQueue[i].props.transaction_id.replace(/[^a-zA-Z0-9]/g, '');
          currQueue[i].props.transaction_id = `${firebaseUser.uid}-${currQueue[i].props.transaction_id}`;
        }

        currQueue[i].props = currQueue[i].props || {};
        currQueue[i].props.user_id = firebaseUser.uid;
        currQueue[i].props = { ...currQueue[i].props, ...params };

        // Original logic (will be overridden by user.gclid/fbclid if it exists)
        currQueue[i].props.gclid = user?.gclid || params.gclid;
        currQueue[i].props.collected_traffic_source = user?.gclid || params.gclid;
        currQueue[i].props.fbclid = user?.fbclid || params.fbclid;

        await trackFirebaseEvent(currQueue[i].eventName, currQueue[i].props);

        await fetch(`${API_URL}/v1/context`, {
          method: 'POST',
          body: JSON.stringify({
            name: currQueue[i].eventName,
            properties: currQueue[i].props,
            params,
          }),
          headers: {
            'Access-Control-Allow-Origin': '*',
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });

        trackSegment(currQueue[i].eventName, currQueue[i].props);
      }
    }
  } catch (err) {
    captureException(err);
    // Fail silently...
  }
};

interface IUpdateIdentity {
  params?: any;
  goal?: string;
  platform?: string;
  viewport?: string;
}

export const updateIdentity = async ({ params, goal, platform, viewport }: IUpdateIdentity) => {
  try {
    const token = (await FirebaseAuthentication.getIdToken()).token;

    if (platform) await setFirebaseAnalyticsUserProperty('platform', platform);
    if (viewport) await setFirebaseAnalyticsUserProperty('viewport', viewport);
    if (goal) await setFirebaseAnalyticsUserProperty('goal', goal);

    await fetch(`${API_URL}/v1/identity`, {
      method: 'POST',
      body: JSON.stringify({ params, platform, viewport, goal }),
      headers: {
        'Access-Control-Allow-Origin': '*',
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
  } catch (err) {
    captureException(err);
    // Fail silently...
  }
};
